import React from 'react';
import { useSnackbarContext } from '../../../contexts/Snackbar';
import { useCompanyContext } from '../../../contexts/Company';
import { useOnboardingContext } from '../../../contexts/Onboarding';
import { useLinesContext } from '../../../contexts/Lines';
import { addProduct, updateProduct } from '../../API';
import cloneDeep from 'lodash.clonedeep';
import messages from '../../../messages';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';

const useStyles = makeStyles((theme: any) => ({
  imgContainer: { display: 'inline-block', marginBottom: theme.spacing(2) },
  img: {
    maxHeight: 280,
    maxWidth: 400,
    borderRadius: 4,
  },
  nameContainer: { display: 'flex', justifyContent: 'center' },
  name: { maxWidth: 700, fontSize: '1.2em', fontWeight: 'bold' },
  buttons: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  button: {
    fontSize: '1.2em',
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  backButton: { color: 'black' },
  confirmButton: { marginLeft: theme.spacing(2) },
  continueAnyway: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(3),
  },
}));

export default function VerifyProduct({ product, tryAgain }) {
  const classes = useStyles();

  const snackbar = useSnackbarContext();

  const { business } = useCompanyContext();

  const { goForward, setLoading } = useOnboardingContext();

  const { products, setProducts } = useLinesContext();

  const newestProduct = products[products?.length - 1];

  async function handleSubmit() {
    const productDetails = {
      asin: product.asin,
      url: product.url || 'https://www.amazon.com/dp/' + product.asin,
      name: product.name,
      image: product.image,
      bestRank: product.bestRank,
      sellerId: business.sellerId,
    };
    try {
      if (newestProduct) {
        if (newestProduct?.asin !== product.asin) {
          setLoading(true);
          await updateProduct(newestProduct?.id, productDetails);
          const updatedProducts = cloneDeep(products).map((product) => {
            if (product.id === newestProduct.id) {
              return { ...product, ...productDetails };
            }
            return product;
          });
          setProducts(updatedProducts);
        }
      } else {
        setLoading(true);
        const product = await addProduct(productDetails);
        const updatedProducts = cloneDeep(products);
        updatedProducts.push({
          id: product.id,
          ...productDetails,
        });
        setProducts(updatedProducts);
      }
      goForward();
    } catch (error) {
      snackbar.show(messages.general.saveError, 'error');
    }
    setLoading(false);
  }

  return (
    <>
      {!product.name && (
        <React.Fragment>
          <Typography variant="h6" paragraph>
            {product.asin}
          </Typography>
          <div className={classes.buttons}>
            <Button
              className={classes.button}
              color="primary"
              variant="contained"
              onClick={tryAgain}
            >
              Let me try another ASIN
            </Button>
          </div>
          <Button
            className={classes.button}
            onClick={handleSubmit}
            data-automation-id={'continue'}
          >
            Continue anyway
          </Button>
        </React.Fragment>
      )}
      {product.name && (
        <React.Fragment>
          {product.image && (
            <Box boxShadow={3} className={classes.imgContainer}>
              {' '}
              <img
                src={product.image}
                alt="product logo"
                className={classes.img}
              />
            </Box>
          )}
          <div className={classes.nameContainer}>
            <Typography paragraph className={classes.name}>
              {product.name}
            </Typography>
          </div>
          <div className={classes.buttons}>
            <Button
              className={classes.button + ' ' + classes.backButton}
              onClick={tryAgain}
            >
              No, let me try another ASIN
            </Button>
            <Button
              className={classes.button + ' ' + classes.confirmButton}
              color="primary"
              variant="contained"
              onClick={handleSubmit}
            >
              Yes, that's it
            </Button>
          </div>
        </React.Fragment>
      )}
    </>
  );
}
